import React, { useEffect } from "react";
import moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/content_wrapper/header";
import CustomLoader from "../../components/customLoader";
import { useGetKeyInvestigation } from "../../hooks/keyManagement/useGetKeyInvestigation";

// ── Types ────────────────────────────────────────────────────────────────────

interface Insight {
  level: "success" | "warning" | "danger" | "info";
  icon: string;
  text: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const KEY_STATUS: Record<number, { label: string; cls: string }> = {
  0: { label: "Pending",           cls: "c-pill--warning" },
  1: { label: "Active",            cls: "c-pill--success" },
  2: { label: "In Use",            cls: "c-pill--primary" },
  3: { label: "Deleted",           cls: "c-pill--danger"  },
  4: { label: "Returned to Owner", cls: "c-pill--secondary" },
};

const SUB_COLORS: Record<string, string> = {
  active:               "#27ae60",
  trialing:             "#27ae60",
  past_due:             "#e67e22",
  unpaid:               "#e67e22",
  canceled:             "#e74c3c",
  incomplete:           "#95a5a6",
  incomplete_expired:   "#95a5a6",
  "":                   "#95a5a6",
};

const EVENT_ICON: Record<string, { icon: string; color: string }> = {
  validation_failed:   { icon: "fa-circle-xmark",  color: "#e74c3c" },
  validation_success:  { icon: "fa-circle-check",  color: "#27ae60" },
  used:                { icon: "fa-circle-check",  color: "#27ae60" },
  created:             { icon: "fa-circle-plus",   color: "#2980b9" },
  expired:             { icon: "fa-clock",          color: "#e67e22" },
  revoked:             { icon: "fa-ban",            color: "#e74c3c" },
  modified:            { icon: "fa-pen",            color: "#2980b9" },
};

const INSIGHT_STYLES: Record<string, { bg: string; border: string; icon: string }> = {
  success: { bg: "#eafaf1", border: "#27ae60", icon: "#27ae60" },
  warning: { bg: "#fef9e7", border: "#e67e22", icon: "#e67e22" },
  danger:  { bg: "#fdf0ef", border: "#e74c3c", icon: "#e74c3c" },
  info:    { bg: "#eaf4fb", border: "#2980b9", icon: "#2980b9" },
};

function ts(unix: number | null | undefined): string {
  if (!unix) return "—";
  return moment.unix(unix).format("DD MMM YYYY HH:mm");
}

function tsRelative(unix: number | null | undefined): string {
  if (!unix) return "—";
  return moment.unix(unix).fromNow();
}

// ── Diagnostic engine ────────────────────────────────────────────────────────

function buildInsights(key: any, events: any[], codes: any, peer: any): Insight[] {
  const insights: Insight[] = [];
  const now = Math.floor(Date.now() / 1000);
  const thirtyDaysAgo = now - 30 * 86400;

  // events come newest-first from backend
  const recent = events.filter(e => e.eventTimestamp >= thirtyDaysAgo);
  const failures = recent.filter(e => !e.success);

  if (recent.length === 0) {
    insights.push({ level: "info", icon: "fa-circle-info", text: "No activity recorded in the last 30 days." });
    return insights;
  }

  const failRate = Math.round((failures.length / recent.length) * 100);

  // Overall health
  if (failRate >= 80) {
    insights.push({ level: "danger", icon: "fa-triangle-exclamation", text: `Critical: ${failRate}% failure rate — ${failures.length} of ${recent.length} events in the last 30 days failed.` });
  } else if (failRate >= 40) {
    insights.push({ level: "warning", icon: "fa-circle-exclamation", text: `Elevated failure rate: ${failRate}% — ${failures.length} of ${recent.length} recent events failed.` });
  } else {
    insights.push({ level: "success", icon: "fa-circle-check", text: `Healthy: ${failRate}% failure rate over the last 30 days (${failures.length} of ${recent.length} events).` });
  }

  // Consecutive failure streak (events are newest-first)
  let streak = 0;
  for (const e of events) {
    if (!e.success) streak++;
    else break;
  }
  if (streak >= 5) {
    insights.push({ level: "danger", icon: "fa-fire", text: `Active failure streak: ${streak} consecutive failures with no successful event since the last success.` });
  } else if (streak >= 2) {
    insights.push({ level: "warning", icon: "fa-fire", text: `Recent failure streak: the last ${streak} events are all failures.` });
  }

  // Dominant error code
  const errMap: Record<string, number> = {};
  failures.forEach((e: any) => { if (e.errorCode) errMap[e.errorCode] = (errMap[e.errorCode] || 0) + 1; });
  const topErr = Object.entries(errMap).sort((a, b) => b[1] - a[1])[0];
  if (topErr && failures.length > 0 && topErr[1] / failures.length >= 0.5) {
    insights.push({ level: "warning", icon: "fa-code", text: `Dominant error: "${topErr[0]}" accounts for ${Math.round(topErr[1] / failures.length * 100)}% of failures (${topErr[1]} occurrences).` });
  }

  // Store concentration
  const storeMap: Record<string, number> = {};
  failures.forEach((e: any) => { if (e.storeName) storeMap[e.storeName] = (storeMap[e.storeName] || 0) + 1; });
  const topStore = Object.entries(storeMap).sort((a, b) => b[1] - a[1])[0];
  if (topStore && failures.length > 0 && topStore[1] / failures.length >= 0.7) {
    insights.push({ level: "warning", icon: "fa-store", text: `Store concentrated: ${Math.round(topStore[1] / failures.length * 100)}% of failures originate from "${topStore[0]}" — may indicate a store-specific issue.` });
  }

  // No active codes + validation failures present
  if (codes.active === 0 && failures.some((e: any) => e.eventType === "validation_failed")) {
    insights.push({ level: "danger", icon: "fa-barcode", text: "No active collection codes — validation failures are likely caused by expired or revoked codes. A new code must be issued." });
  }

  // Subscription problems
  if (["past_due", "canceled", "unpaid", "incomplete_expired"].includes(key.subscriptionActiveStatus)) {
    insights.push({ level: "danger", icon: "fa-credit-card", text: `Subscription is "${key.subscriptionActiveStatus}" — key access is likely restricted until payment is resolved.` });
  }

  // Peer comparison
  if (peer && peer.hostKeyCount > 1 && peer.host30dTotal > 0) {
    const hostFailRate = Math.round(peer.host30dFailures / peer.host30dTotal * 100);
    const thisFailRate = peer.thisKey30dTotal > 0 ? Math.round(peer.thisKey30dFailures / peer.thisKey30dTotal * 100) : 0;
    if (thisFailRate > hostFailRate + 20) {
      insights.push({ level: "warning", icon: "fa-chart-bar", text: `Outlier: this key fails ${thisFailRate}% vs ${hostFailRate}% average for other keys at this host (${peer.hostKeyCount} keys compared).` });
    } else if (hostFailRate > 50) {
      insights.push({ level: "warning", icon: "fa-chart-bar", text: `Host-wide issue: ${hostFailRate}% failure rate across all ${peer.hostKeyCount} keys at this host — may indicate a systemic problem, not specific to this key.` });
    }
  }

  return insights;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function InsightCard({ insight }: { insight: Insight }) {
  const s = INSIGHT_STYLES[insight.level];
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start", background: s.bg, border: `1px solid ${s.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 8 }}>
      <i className={`fa-solid ${insight.icon}`} style={{ color: s.icon, marginTop: 2, flexShrink: 0 }} />
      <span style={{ fontSize: 13, lineHeight: 1.5 }}>{insight.text}</span>
    </div>
  );
}

function CodeSummaryCard({ codes }: { codes: any }) {
  const items = [
    { label: "Active",   count: codes.active,   color: "#27ae60" },
    { label: "Expired",  count: codes.expired,  color: "#e67e22" },
    { label: "Revoked",  count: codes.revoked,  color: "#e74c3c" },
    { label: "Depleted", count: codes.depleted, color: "#95a5a6" },
  ];
  return (
    <div className="card h-100">
      <div className="card-body">
        <p className="fw-semibold mb-3" style={{ fontSize: 13 }}>
          <i className="fa-solid fa-barcode me-2 text-warning" />
          Collection Codes
        </p>
        <div className="d-flex justify-content-between mb-2">
          <span style={{ fontSize: 13, color: "#666" }}>Total issued</span>
          <span className="fw-semibold">{codes.total}</span>
        </div>
        {items.map(({ label, count, color }) => (
          <div key={label} className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}>
            <span style={{ color: "#666" }}>{label}</span>
            <span className="fw-semibold" style={{ color }}>{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PeerCard({ peer }: { peer: any }) {
  if (!peer) {
    return (
      <div className="card h-100">
        <div className="card-body d-flex align-items-center justify-content-center text-muted" style={{ fontSize: 13 }}>
          <div className="text-center">
            <i className="fa-solid fa-chart-bar mb-2 d-block" style={{ fontSize: 22, opacity: 0.3 }} />
            No host data — key is not currently at a host location
          </div>
        </div>
      </div>
    );
  }
  const thisRate = peer.thisKey30dTotal > 0 ? Math.round(peer.thisKey30dFailures / peer.thisKey30dTotal * 100) : null;
  const hostRate = peer.host30dTotal > 0 ? Math.round(peer.host30dFailures / peer.host30dTotal * 100) : null;
  const isOutlier = thisRate !== null && hostRate !== null && thisRate > hostRate + 20;

  return (
    <div className="card h-100">
      <div className="card-body">
        <p className="fw-semibold mb-3" style={{ fontSize: 13 }}>
          <i className="fa-solid fa-chart-bar me-2 text-primary" />
          Peer Comparison (30d)
        </p>
        <div className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}>
          <span style={{ color: "#666" }}>Keys at same host</span>
          <span className="fw-semibold">{peer.hostKeyCount}</span>
        </div>
        <div className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}>
          <span style={{ color: "#666" }}>This key failure rate</span>
          <span className="fw-semibold" style={{ color: isOutlier ? "#e74c3c" : undefined }}>
            {thisRate !== null ? `${thisRate}%` : "—"}
          </span>
        </div>
        <div className="d-flex justify-content-between mb-2" style={{ fontSize: 13 }}>
          <span style={{ color: "#666" }}>Host avg failure rate</span>
          <span className="fw-semibold">{hostRate !== null ? `${hostRate}%` : "—"}</span>
        </div>
        {isOutlier && (
          <div className="alert alert-warning py-1 px-2 mb-0" style={{ fontSize: 12 }}>
            This key is performing significantly worse than its peers.
          </div>
        )}
      </div>
    </div>
  );
}

function ActivityCard({ events }: { events: any[] }) {
  const now = Math.floor(Date.now() / 1000);
  const last24h = events.filter(e => e.eventTimestamp >= now - 86400);
  const last7d  = events.filter(e => e.eventTimestamp >= now - 7 * 86400);
  const latest  = events[0]; // newest first

  return (
    <div className="card h-100">
      <div className="card-body">
        <p className="fw-semibold mb-3" style={{ fontSize: 13 }}>
          <i className="fa-solid fa-clock-rotate-left me-2 text-secondary" />
          Activity Summary
        </p>
        <div className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}>
          <span style={{ color: "#666" }}>Events (24h)</span>
          <span className="fw-semibold">{last24h.length}</span>
        </div>
        <div className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}>
          <span style={{ color: "#666" }}>Events (7d)</span>
          <span className="fw-semibold">{last7d.length}</span>
        </div>
        <div className="d-flex justify-content-between mb-1" style={{ fontSize: 13 }}>
          <span style={{ color: "#666" }}>Total loaded</span>
          <span className="fw-semibold">{events.length}</span>
        </div>
        <div className="d-flex justify-content-between" style={{ fontSize: 13 }}>
          <span style={{ color: "#666" }}>Last event</span>
          <span className="fw-semibold">{latest ? tsRelative(latest.eventTimestamp) : "—"}</span>
        </div>
      </div>
    </div>
  );
}

function Timeline({ events }: { events: any[] }) {
  // events come newest-first; reverse for chronological display
  const chronological = [...events].reverse();

  if (chronological.length === 0) {
    return <div className="text-muted text-center py-5" style={{ fontSize: 13 }}>No events recorded for this key.</div>;
  }

  return (
    <div style={{ position: "relative" }}>
      {chronological.length > 1 && (
        <div style={{ position: "absolute", left: 5, top: 8, bottom: 8, width: 2, background: "#dee2e6" }} />
      )}
      {chronological.map((event: any, idx: number) => {
        const def = EVENT_ICON[event.eventType] ?? { icon: "fa-circle", color: "#bdc3c7" };
        const isFailure = !event.success;
        return (
          <div key={event._id ?? idx} style={{ display: "flex", gap: 14, marginBottom: 14, position: "relative", zIndex: 1 }}>
            {/* dot */}
            <div style={{
              flexShrink: 0, width: 12, height: 12, borderRadius: "50%",
              marginTop: 3, background: def.color,
              outline: `3px solid ${def.color}22`,
            }} />
            {/* content */}
            <div style={{
              flex: 1, paddingBottom: 12,
              borderBottom: idx < chronological.length - 1 ? "1px solid #f5f5f5" : "none",
              background: isFailure ? "#fff5f5" : undefined,
              borderRadius: isFailure ? 4 : undefined,
              padding: isFailure ? "4px 8px 8px" : undefined,
            }}>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-1">
                <div>
                  <span className="fw-semibold" style={{ fontSize: 13, color: def.color }}>
                    {(event.eventType ?? "—").replace(/_/g, " ")}
                  </span>
                  {event.operation && (
                    <span className="ms-2 badge" style={{ background: "#ecf0f1", color: "#555", fontSize: 11 }}>
                      {event.operation.replace(/_/g, " ")}
                    </span>
                  )}
                  {event.success
                    ? <span className="ms-2 badge bg-success" style={{ fontSize: 10 }}>success</span>
                    : <span className="ms-2 badge bg-danger"  style={{ fontSize: 10 }}>failed</span>
                  }
                </div>
                <span style={{ fontSize: 11, color: "#999", whiteSpace: "nowrap" }}>
                  {ts(event.eventTimestamp)}
                </span>
              </div>

              <div className="d-flex flex-wrap gap-3 mt-1" style={{ fontSize: 12, color: "#666" }}>
                {event.storeName && <span><i className="fa-solid fa-store me-1" />{event.storeName}</span>}
                {event.actorName && <span><i className="fa-solid fa-user me-1" />{event.actorName}</span>}
                {event.code && event.code !== "***" && (
                  <span className="font-monospace" style={{ fontSize: 11 }}>
                    <i className="fa-solid fa-barcode me-1" />{event.code}
                  </span>
                )}
              </div>

              {isFailure && (event.errorCode || event.failureReason) && (
                <div className="mt-1 d-flex flex-wrap gap-2" style={{ fontSize: 12 }}>
                  {event.errorCode && (
                    <span className="badge" style={{ background: "#fde8e8", color: "#c0392b", fontFamily: "monospace" }}>
                      {event.errorCode}
                    </span>
                  )}
                  {event.failureReason && (
                    <span style={{ color: "#c0392b" }}>{event.failureReason}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {events.length >= 200 && (
        <p className="text-muted text-center mt-2" style={{ fontSize: 12 }}>
          Showing last 200 events. Use Code Audit Logs for full history.
        </p>
      )}
    </div>
  );
}

// ── Screen ───────────────────────────────────────────────────────────────────

const KeyInvestigationScreen: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const keyId = location.state?.id as string | undefined;

  useEffect(() => {
    if (!keyId) navigate("/keymanagementlisting", { replace: true });
  }, [keyId, navigate]);

  const { data: rawData, isLoading, isError } = useGetKeyInvestigation(keyId);
  const inv = rawData?.data;

  if (isLoading) {
    return (
      <div className="content-wrapper">
        <Header title="Key Investigation" />
        <div className="main-wrapper p-4"><CustomLoader /></div>
      </div>
    );
  }

  if (isError || !inv) {
    return (
      <div className="content-wrapper">
        <Header title="Key Investigation" />
        <div className="main-wrapper p-4">
          <div className="alert alert-danger">Failed to load investigation data. The key may not exist.</div>
        </div>
      </div>
    );
  }

  const { key, auditEvents, codesSummary, peerComparison } = inv;
  const insights = buildInsights(key, auditEvents, codesSummary, peerComparison);
  const statusInfo = KEY_STATUS[key.status] ?? { label: "Unknown", cls: "" };
  const subColor = SUB_COLORS[key.subscriptionActiveStatus ?? ""] ?? "#95a5a6";

  return (
    <div className="content-wrapper">
      <Header title="Key Investigation" />
      <div className="main-wrapper p-4">

        {/* Back + actions */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <button className="btn btn-sm btn-outline-secondary" onClick={() => navigate(-1)}>
            <i className="fa-solid fa-arrow-left me-2" />Back
          </button>
          <button
            className="btn btn-sm btn-outline-primary"
            onClick={() => navigate("/keymanagementdetail", { state: { id: keyId } })}
          >
            <i className="fa-solid fa-key me-2" />View Full Detail
          </button>
        </div>

        {/* Key hero */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center justify-content-center rounded-circle bg-light" style={{ width: 52, height: 52, flexShrink: 0 }}>
                  <i className="fa-solid fa-microscope fs-4" style={{ color: "#72bcb8" }} />
                </div>
                <div>
                  <h4 className="mb-1 fw-bold">{key.keyName || "—"}</h4>
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <span className={`c-pill ${statusInfo.cls}`}>{statusInfo.label}</span>
                    {key.subscriptionActiveStatus && (
                      <span className="badge" style={{ background: subColor, fontSize: 11 }}>
                        {key.subscriptionActiveStatus}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ fontSize: 12, color: "#666", textAlign: "right" }}>
                {key.keyringCode && (
                  <div className="font-monospace mb-1">
                    <i className="fa-solid fa-key me-1" />
                    {key.keyringCode}
                  </div>
                )}
                <div>Created {key.createdAt ? moment(key.createdAt).format("DD MMM YYYY") : "—"}</div>
                {key.lastActivityAt && (
                  <div>Last active {tsRelative(key.lastActivityAt)}</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Diagnostic Insights */}
        <div className="mb-4">
          <h6 className="fw-semibold mb-3" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.05em", color: "#888" }}>
            <i className="fa-solid fa-magnifying-glass me-2" />Diagnostic Insights
          </h6>
          {insights.map((ins, i) => <InsightCard key={i} insight={ins} />)}
        </div>

        {/* Stats row */}
        <div className="row g-3 mb-4">
          <div className="col-md-4">
            <CodeSummaryCard codes={codesSummary} />
          </div>
          <div className="col-md-4">
            <PeerCard peer={peerComparison} />
          </div>
          <div className="col-md-4">
            <ActivityCard events={auditEvents} />
          </div>
        </div>

        {/* Timeline */}
        <div className="card">
          <div className="card-body">
            <h6 className="fw-semibold mb-4" style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: "0.05em", color: "#888" }}>
              <i className="fa-solid fa-timeline me-2" />Event Timeline
              <span className="ms-2 badge bg-light text-dark fw-normal" style={{ fontSize: 11 }}>
                {auditEvents.length} events · oldest → newest
              </span>
            </h6>
            <Timeline events={auditEvents} />
          </div>
        </div>

      </div>
    </div>
  );
};

export default KeyInvestigationScreen;
